import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

const NARROW_W = 420;

export const getFuturesPickerModalStyles = (theme: Theme, windowWidth: number, windowHeight: number) => {
  const narrow = windowWidth < NARROW_W;
  const modalListMaxHeight = Math.min(windowHeight * 0.62, 580);
  const modalSheetMaxHeight = Math.min(windowHeight * 0.85, modalListMaxHeight + 120);

  return StyleSheet.create({
    modalRoot: {
      flex: 1,
      justifyContent: 'flex-end',
    },
    modalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: theme.background.overlay,
    },
    modalSheet: {
      backgroundColor: theme.background.card,
      borderTopLeftRadius: 22,
      borderTopRightRadius: 22,
      borderWidth: 1,
      borderColor: theme.gray[200],
      borderBottomWidth: 0,
      maxHeight: modalSheetMaxHeight,
      width: '100%',
      paddingBottom: Platform.OS === 'ios' ? 34 : 16,
    },
    modalSheetSaving: {
      opacity: 0.72,
    },
    modalHandle: {
      width: 36,
      height: 4,
      borderRadius: 2,
      backgroundColor: theme.gray[300],
      alignSelf: 'center',
      marginTop: 12,
      marginBottom: 4,
    },
    modalHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: narrow ? 12 : 16,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.gray[200],
      gap: 8,
    },
    modalTitleWrap: {
      flex: 1,
      minWidth: 0,
    },
    modalTitle: {
      fontSize: narrow ? 14 : 15,
      fontWeight: '800',
      color: theme.gray[800],
    },
    modalCloseBtn: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: theme.gray[100],
      justifyContent: 'center',
      alignItems: 'center',
      flexShrink: 0,
    },
    modalCloseBtnText: {
      fontSize: 13,
      color: theme.gray[500],
      fontWeight: '700',
    },
    modalFlashListWrap: {
      width: '100%',
      height: modalListMaxHeight,
    },
    modalFlashList: {
      flex: 1,
    },
    modalList: {
      paddingHorizontal: 10,
      paddingTop: 6,
      paddingBottom: 16,
    },

    // ── Option row (shared by winner + scorer) ──────────────────────────────
    optionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingVertical: 9,
      paddingHorizontal: 8,
      borderRadius: 10,
      marginBottom: 3,
    },
    optionRowSelected: {
      backgroundColor: theme.accent + '1E',
    },
    optionRowPressed: {
      backgroundColor: theme.gray[100],
    },
    optionRowDisabled: {
      opacity: 0.55,
    },
    optionRowOtherFooter: {
      marginTop: 6,
    },

    // Flag icon container
    optionFlagWrap: {
      width: 40,
      height: 27,
      borderRadius: 4,
      overflow: 'hidden',
      backgroundColor: theme.gray[100],
      flexShrink: 0,
    },

    // Kit icon container (same width as flag for alignment)
    optionKitWrap: {
      width: 40,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },

    // Text + bar column
    optionContent: {
      flex: 1,
      minWidth: 0,
      gap: 5,
    },
    optionTopRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    optionName: {
      flex: 1,
      minWidth: 0,
      fontSize: narrow ? 13 : 14,
      fontWeight: '600',
      color: theme.gray[700],
    },
    optionNameSelected: {
      color: theme.accentDark,
      fontWeight: '700',
    },

    // ── Odd badge ───────────────────────────────────────────────────────────
    oddBadge: {
      backgroundColor: theme.gray[100],
      borderRadius: 5,
      paddingHorizontal: 5,
      paddingVertical: 2,
      flexShrink: 0,
    },
    oddBadgeText: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.gray[500],
    },

    // ── Pick % bar ──────────────────────────────────────────────────────────
    pickBarTrack: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    pickBarOuter: {
      flex: 1,
      height: 4,
      borderRadius: 2,
      backgroundColor: theme.gray[200],
      overflow: 'hidden',
    },
    pickBarFill: {
      height: 4,
      borderRadius: 2,
      backgroundColor: theme.accent + '88',
      minWidth: 2,
    },
    pickBarFillSelected: {
      backgroundColor: theme.accent,
    },
    pickPctText: {
      fontSize: 11,
      fontWeight: '600',
      color: theme.gray[400],
      width: 30,
      textAlign: 'right',
      flexShrink: 0,
    },
    pickPctTextSelected: {
      color: theme.accentDark,
    },
  });
};
